'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  FileTextIcon,
  HeadsetIcon,
  HelpCircleIcon,
  MailIcon,
  MessageCircleIcon,
  PhoneForwardedIcon,
  PhoneIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react';

import { useInjectKeyframes } from '@kit/ui/animations';
import { Slider } from '@kit/ui/slider';
import { cn } from '@kit/ui/utils';

import {
  AxcelerateBrandIcon,
  AxcelerateDestination,
  LeagenBrandIcon,
} from './brand-icons';
import {
  type Edge,
  FlowChart,
  type FlowLayout,
  FlowList,
  type FlowNode,
  finalizeLayout,
} from './flow';
import styles from './roi-calculator.module.css';

// ---------- Salaries ----------

const SALARIES = {
  generalist: 60_000,
  receptionist: 55_000,
  enrolmentOfficer: 65_000,
  salesOfficer: 70_000,
  supportOfficer: 60_000,
};

const IVR_ANNUAL_COST = 3_600;

// Expected Leagen-side volume reductions — reflects AI absorbing most
// enquiries. Used for the Leagen cost / staff calc AND the chart's
// channel volume annotations + the summary footer narrative.
//   Phone halved (2× reduction) — chatbot absorbs simple FAQs before
//   the caller picks up the phone.
//   Email quartered (4× reduction) — AI answers most enquiries
//   instantly; only complex cases still generate a human-facing email.
//   Manual enrolments quartered (4× reduction) — students self-serve
//   via the AI-assisted path, so very few still need a human to click
//   enrol on their behalf.
const LEAGEN_PHONE_REDUCTION = 0.5; // phone calls drop to 50% of original
const LEAGEN_EMAIL_REDUCTION = 0.75; // email drops to 25% of original
const LEAGEN_MANUAL_REDUCTION = 0.75; // manual % drops to 25% of original
// Average time humans spend on each inbound interaction, used by the
// Growth Capacity cards to turn "reduction %" into a concrete
// hours-freed-per-month figure. Conservative industry defaults:
// 10 min per phone call (discovery/troubleshooting), 5 min per email
// (read + draft + send).
const AVG_PHONE_MIN = 10;
const AVG_EMAIL_MIN = 5;

// ---------- Helpers ----------

function formatCurrency(amount: number) {
  if (amount >= 1_000_000) return `A$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `A$${Math.round(amount / 1_000)}k`;
  // Sub-$1k values round to the nearest dollar rather than getting
  // smeared into a misleading "A$1k" by the integer-k formatter.
  return `A$${Math.round(amount).toLocaleString()}`;
}

function formatCostBadge(annual: number) {
  if (annual >= 1_000_000) return `$${(annual / 1_000_000).toFixed(1)}M`;
  if (annual >= 1_000) return `$${Math.round(annual / 1_000)}k`;
  return `$${annual}`;
}

// Shown on the Traditional side when the RTO has declared zero
// channels, zero staff, zero infrastructure — a "you have literally
// nothing, Leagen is your only option" moment, played for laughs.
function EmptyTeamMessage(props: { hasEnquiryChannels: boolean }) {
  return (
    <div className="border-border bg-muted/20 flex aspect-[4/3] flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-10 text-center">
      <div className="text-5xl">😱</div>
      <p className="mt-4 text-lg font-semibold">
        You are in desperate need of Leagen.
      </p>
      <p className="text-muted-foreground mt-2 text-sm">
        {props.hasEnquiryChannels
          ? "You've got enquiries coming in — phone, email — but no staff to answer them. Leagen's AI can pick up every call and email 24/7 while you sleep."
          : 'No channels, no staff, no way to enrol anyone. Leagen can save you — the AI answers every enquiry while you sleep.'}
      </p>
    </div>
  );
}

// Shown on the Leagen side when the RTO has no human handlers (no
// officers and no receptionist). The AI's enquiry/chat path still
// works, but ASQA's AI-in-enrolment guidance + Privacy Act APP 1.8
// require a human-review / human-escalation pathway for any
// AI-assisted enrolment decision — without one, Leagen cannot legally
// participate in the enrolment process.
function LeagenComplianceNotice() {
  return (
    <div className="mb-4 rounded-2xl border-2 border-dashed border-amber-500/40 bg-amber-500/5 px-5 py-4">
      <div className="flex items-start gap-3">
        <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
        <div className="space-y-1.5">
          <p className="text-sm font-semibold">
            A human handler is required for AI-assisted enrolment.
          </p>
          <p className="text-muted-foreground text-xs leading-relaxed">
            ASQA&apos;s AI guidance and the Privacy Act APP 1.8 require an RTO
            to maintain a human-escalation pathway and human-review route for
            automated enrolment decisions. Add at least one Enrolment / Sales /
            Support Officer, or turn on Receptionist, to continue.
          </p>
          <p className="text-muted-foreground/70 text-[11px] italic">
            aXcelerate online enrolment form and AI chat still work —
            they&apos;re self-service. The notice only applies to human- touch
            escalation paths.
          </p>
        </div>
      </div>
    </div>
  );
}

// A cheap structural hash of a flow layout — used as the FlowChart's
// React key so the chart fully remounts whenever a node is added,
// removed, or moves to a new column/row. Without this, AnimatePresence
// occasionally leaves an exiting edge's stale path in the SVG when a
// rapid toggle happens (the "arrows getting stuck" bug).
function flowStructureKey(layout: FlowLayout): string {
  const nodes = layout.nodes
    .map((n) => `${n.id}@${n.col}.${n.row}×${n.colSpan ?? 1}`)
    .sort()
    .join('|');
  const edges = layout.edges
    .map((e) => `${e.from}>${e.to}:${e.tone ?? ''}`)
    .sort()
    .join('|');
  return `${layout.cols}::${nodes}::${edges}`;
}

// Build the IVR menu subtitle from whichever press-branches actually
// exist. Used by both Traditional and Leagen IVR cards so the copy
// never promises a "Press 3 support" branch that doesn't exist.
function buildIvrSubtitle(branches: {
  receptionist: boolean;
  enrolments: boolean;
  support: boolean;
}): string {
  const steps: string[] = [];
  let step = 1;
  if (branches.receptionist) steps.push(`${step++} reception`);
  if (branches.enrolments) steps.push(`${step++} enrolments`);
  if (branches.support) steps.push(`${step++} support`);
  return steps.length > 0
    ? `Press ${steps.join(', ')}`
    : 'Routes all inbound calls';
}

function axFormNode(col = 1, colSpan = 1, row = 2): FlowNode {
  return {
    id: 'axform',
    kind: 'profile',
    customIcon: <AxcelerateBrandIcon />,
    title: 'aXcelerate Online Enrolment Form',
    subtitle: 'Students self-enrol — no human touches it',
    tone: 'traditional',
    col,
    colSpan,
    row,
  };
}

// ---------- Inputs shape ----------

export type TeamMode = 'combined' | 'separate';

export type TraditionalInputs = {
  monthlyEnrolments: number;
  manualPct: number; // 0–100
  phoneCalls: number;
  emailEnquiries: number;
  hasOnlineForm: boolean;
  takesPhone: boolean;
  takesEmail: boolean;
  hasReceptionist: boolean;
  hasIvr: boolean;
  teamMode: TeamMode;
  combinedCount: number;
  enrolmentOfficers: number;
  salesOfficers: number;
  supportOfficers: number;
};

// ---------- Traditional flow ----------

export function buildTraditionalFlow(
  inputs: TraditionalInputs,
): FlowLayout & { totalCost: number; staffCount: number } {
  const {
    manualPct,
    phoneCalls,
    emailEnquiries,
    hasOnlineForm,
    takesPhone,
    takesEmail,
    hasReceptionist,
    hasIvr,
    teamMode,
    combinedCount,
    enrolmentOfficers,
    salesOfficers,
    supportOfficers,
  } = inputs;

  const hiddenChannels = new Set<string>();
  if (!takesPhone || phoneCalls === 0) hiddenChannels.add('c-phone');
  if (!takesEmail || emailEnquiries === 0) hiddenChannels.add('c-web');
  // When the aXcelerate online enrolment form is turned off, hide the
  // whole Enrolment Form column (channel + axform card). remapLanes
  // will then slide the remaining lanes left to fill the space.
  if (!hasOnlineForm) {
    hiddenChannels.add('c-form');
    hiddenChannels.add('axform');
  }

  const useCombined = teamMode === 'combined';
  const showSales = !useCombined && salesOfficers > 0;
  const showSupport = !useCombined && supportOfficers > 0;
  const showEnrolment = !useCombined && enrolmentOfficers > 0;

  // Total cost — sum of every human on the chart plus IVR if toggled.
  const staffCost = useCombined
    ? combinedCount * SALARIES.generalist
    : enrolmentOfficers * SALARIES.enrolmentOfficer +
      salesOfficers * SALARIES.salesOfficer +
      supportOfficers * SALARIES.supportOfficer;
  const receptionistCost = hasReceptionist ? SALARIES.receptionist : 0;
  const ivrCost = hasIvr ? IVR_ANNUAL_COST : 0;
  const totalCost = staffCost + receptionistCost + ivrCost;
  const staffCount =
    (useCombined
      ? combinedCount
      : enrolmentOfficers + salesOfficers + supportOfficers) +
    (hasReceptionist ? 1 : 0);

  const nodes: FlowNode[] = [
    {
      id: 'c-form',
      kind: 'channel',
      icon: FileTextIcon,
      label: 'Enrolment Form',
      col: 1,
      row: 1,
    },
    {
      id: 'c-web',
      kind: 'channel',
      icon: MailIcon,
      label: 'Email Enquiry',
      col: 2,
      row: 1,
      volume: `${emailEnquiries.toLocaleString()}/mo`,
    },
    {
      id: 'c-phone',
      kind: 'channel',
      icon: PhoneIcon,
      label: 'Phone Call',
      col: 3,
      row: 1,
      volume: `${phoneCalls.toLocaleString()}/mo`,
    },
    axFormNode(1, 1, 2),
  ];

  const edges: Edge[] = [
    { from: 'c-form', to: 'axform' },
    { from: 'axform', to: 'ax' },
  ];

  if (useCombined) {
    // One card that does every role. When there's no Receptionist or IVR
    // sitting in col 3, Admin spans cols 2-3 at row 2 so phone + web both
    // feed straight into it. When a phone-router card occupies col 3 at
    // row 2, Admin drops to row 3 in col 2 so the router→admin edge is a
    // clean forward drop instead of a same-row back-edge that would wrap
    // around the left of the Admin card.
    const hasPhoneRouter = hasReceptionist || hasIvr;
    // Admin column choice — align the card with its actual primary
    // input lane so the feeding edge is a straight vertical drop
    // rather than a diagonal:
    //   - If both email and phone survive: span cols 2-3 (catches both).
    //   - If only email survives: col 2 (below Email Enquiry).
    //   - If only phone survives: col 3 (below Phone Call / router).
    //   - If neither survives: col 2 span 2 (safe default, pruned by
    //     the empty-team renderer anyway).
    //   - When a phone-router exists it occupies col 3 row 2, so Admin
    //     drops to col 2 row 3 (colSpan 1) regardless of email.
    const emailVisible = takesEmail && emailEnquiries > 0;
    const phoneVisible = takesPhone && phoneCalls > 0;
    let adminCol = 2;
    let adminSpan: number = hasPhoneRouter ? 1 : 2;
    if (hasPhoneRouter) {
      adminCol = 2;
      adminSpan = 1;
    } else if (emailVisible && phoneVisible) {
      adminCol = 2;
      adminSpan = 2;
    } else if (phoneVisible && !emailVisible) {
      adminCol = 3;
      adminSpan = 1;
    } else {
      adminCol = 2;
      adminSpan = 1;
    }
    nodes.push({
      id: 'admin',
      kind: 'profile',
      icon: UsersIcon,
      title: combinedCount > 1 ? 'Admin Team' : 'Admin Generalist',
      subtitle: 'Phones, enrolments, follow-ups, support',
      cost: formatCostBadge(combinedCount * SALARIES.generalist),
      count: combinedCount,
      tone: 'traditional',
      status: {
        tone: 'amber',
        label: combinedCount > 1 ? 'Stretched' : 'Bottleneck',
      },
      col: adminCol,
      colSpan: adminSpan,
      row: hasPhoneRouter ? 3 : 2,
    });
    // Optional IVR + Receptionist still route through the same admin card.
    if (hasIvr) {
      nodes.push({
        id: 'ivr',
        kind: 'profile',
        icon: PhoneForwardedIcon,
        title: 'IVR Phone Routing',
        subtitle: 'Routes all calls to admin',
        cost: formatCostBadge(IVR_ANNUAL_COST),
        costSub: '/yr',
        tone: 'system',
        col: 3,
        row: 2,
      });
      edges.push({ from: 'c-phone', to: 'ivr' });
      edges.push({ from: 'ivr', to: 'admin' });
    } else if (hasReceptionist) {
      nodes.push({
        id: 'reception',
        kind: 'profile',
        icon: HeadsetIcon,
        title: 'Receptionist',
        subtitle: 'Routes inbound calls',
        cost: formatCostBadge(SALARIES.receptionist),
        tone: 'traditional',
        col: 3,
        row: 2,
      });
      edges.push({ from: 'c-phone', to: 'reception' });
      edges.push({ from: 'reception', to: 'admin' });
    } else {
      edges.push({ from: 'c-phone', to: 'admin' });
    }
    edges.push({ from: 'c-web', to: 'admin' });
    edges.push({ from: 'admin', to: 'ax' });
    nodes.push({
      id: 'ax',
      kind: 'destination',
      label: 'aXcelerate',
      col: 1,
      colSpan: 3,
      row: hasPhoneRouter ? 4 : 3,
    });
    return finalizeLayout(
      { ...{ cols: 3, nodes, edges, totalCost, staffCount } },
      hiddenChannels,
      1,
    );
  }

  // ---------- Separate-roles mode ----------
  // Roles live in a 4-col grid so Sales / Enrolment / Support can each have
  // their own lane. Sales sits in col 2 (the web lane's destination), Enrolment
  // in col 3, Support in col 4. Receptionist / IVR share the phone lane
  // route but the 3-button IVR needs three downstream destinations.

  // Determine grid width based on how many role lanes we need.
  // Channels always at row 1 in cols 1-3 regardless.
  const _roleCols = [
    showSales ? 'sales' : null,
    'enrolment',
    showSupport ? 'support' : null,
  ].filter((x) => x !== null) as string[];
  // axform + role cards share row 2-3. We put role cards at row 3 so IVR
  // or Receptionist can sit at row 2 like L2 traditional before.
  const hasPhoneRouter = hasIvr || hasReceptionist;
  // When IVR, Receptionist AND Support all exist, col 3 can't hold all
  // three vertically (IVR → Support line would cross the Receptionist
  // card). Push Receptionist out to col 4 so IVR → Support is a clean
  // vertical drop and IVR → Receptionist is a clean horizontal hop.
  const receptionToCol4 = hasIvr && hasReceptionist && showSupport;

  if (hasPhoneRouter) {
    if (hasIvr) {
      nodes.push({
        id: 'ivr',
        kind: 'profile',
        icon: PhoneForwardedIcon,
        title: 'IVR Phone Routing',
        subtitle: buildIvrSubtitle({
          receptionist: hasReceptionist,
          enrolments: showSales || showEnrolment,
          support: showSupport,
        }),
        cost: formatCostBadge(IVR_ANNUAL_COST),
        costSub: '/yr',
        tone: 'system',
        col: 3,
        row: 2,
      });
      edges.push({ from: 'c-phone', to: 'ivr' });
    }
    if (hasReceptionist) {
      // When reception is the ONLY human on the Traditional team (no
      // officers of any kind, no IVR), the receptionist has to cover
      // the email lane AND the phone lane themselves — and do manual
      // enrolments off the back of those calls/emails. Widen to span
      // col 2-3 so email + phone both drop STRAIGHT DOWN into reception.
      // "Reception only" = reception is the sole human handler. The
      // IVR flag doesn't disqualify it: a small RTO that has IVR +
      // reception but no officers still has reception as the only
      // human, and reception must cover the email lane too (otherwise
      // the email channel has nowhere to go). We keep the card narrow
      // (col 3 under phone) when IVR is present because IVR needs
      // col 3, and rely on the extra c-web → reception edge below to
      // feed email in via a short diagonal.
      const tradReceptionOnly = !showSales && !showEnrolment && !showSupport;
      const _tradReceptionOnlyNoIvr = tradReceptionOnly && !hasIvr;
      nodes.push({
        id: 'reception',
        kind: 'profile',
        icon: HeadsetIcon,
        title: 'Receptionist',
        subtitle: tradReceptionOnly
          ? 'Answers enquiries + does manual enrolments'
          : hasIvr
            ? 'General line only'
            : 'Routes all inbound calls',
        cost: formatCostBadge(SALARIES.receptionist),
        tone: 'traditional',
        // Reception-only (with or without IVR) always widens to col 2-3
        // so Email Enquiry (col 2) drops STRAIGHT DOWN into reception
        // instead of requiring a diagonal edge from col 2 to col 3.
        // IVR at col 3 row 2 doesn't collide with reception at col 2-3
        // row 3 — different row. receptionToCol4 is the "IVR + support
        // + reception" case where reception jumps to col 4 to clear
        // the IVR→support drop, which stays as-is.
        col: tradReceptionOnly ? 2 : receptionToCol4 ? 4 : 3,
        colSpan: tradReceptionOnly ? 2 : 1,
        row: tradReceptionOnly ? 3 : receptionToCol4 ? 3 : hasIvr ? 3 : 2,
      });
      if (hasIvr) {
        edges.push({ from: 'ivr', to: 'reception' });
      } else {
        edges.push({ from: 'c-phone', to: 'reception' });
      }
      if (tradReceptionOnly) {
        // Reception is the only human — email drops into them too
        // (whether or not IVR sits in col 3), and reception is the
        // one doing manual enrolment into aXcelerate. Without the
        // c-web → reception edge, the email channel has no handler
        // when IVR is on and no officers are configured.
        edges.push({ from: 'c-web', to: 'reception' });
        edges.push({ from: 'reception', to: 'ax' });
      }
    }
  }

  // Sales + Enrolment live under the EMAIL lane (col 2) by default —
  // sales converts incoming enquiries, enrolment processes the signups.
  // But when Email Enquiry is turned off (c-web is hidden), col 2 has no
  // channel above and becomes an empty visual lane. In that case we move
  // sales/enrolment into col 3 (the phone lane) so they sit directly
  // below Phone Call — provided col 3 isn't already claimed by a
  // phone-router (IVR/Receptionist) or a Support Officer card. This
  // collapses the chart to 2 columns (form + phone) for the "phone-only"
  // small-RTO scenario, following the "prefer a straight line" rule.
  const emailVisible = takesEmail && emailEnquiries > 0;
  const col3ClaimedByPhoneStack = hasIvr || hasReceptionist || showSupport;
  const salesLaneCol = !emailVisible && !col3ClaimedByPhoneStack ? 3 : 2;

  if (showSales) {
    nodes.push({
      id: 'sales',
      kind: 'profile',
      icon: UserPlusIcon,
      title: salesOfficers > 1 ? 'Sales Officers' : 'Sales Officer',
      subtitle: 'Handles enquiries — converts to enrolments',
      cost: formatCostBadge(salesOfficers * SALARIES.salesOfficer),
      count: salesOfficers,
      tone: 'traditional',
      col: salesLaneCol,
      row: 3,
    });
    if (emailVisible) edges.push({ from: 'c-web', to: 'sales' });
    if (hasIvr) {
      edges.push({ from: 'ivr', to: 'sales' });
    } else if (hasReceptionist) {
      edges.push({ from: 'reception', to: 'sales' });
    } else {
      edges.push({ from: 'c-phone', to: 'sales' });
    }
  }

  // Enrolment Officer — the role that actually presses "enrol" in
  // aXcelerate. Stacks under Sales when Sales is present (same column,
  // row 4), otherwise it takes Sales' job of receiving from email +
  // phone directly. When no sales AND no col-3 competitor AND no email,
  // enrolment sits in col 3 under Phone; when no sales AND email is
  // visible AND no col-3 competitor, it spans col 2-3 so web + phone
  // both feed straight in.
  if (showEnrolment) {
    const col3Occupied = hasIvr || hasReceptionist || showSupport;
    const span = !showSales && emailVisible && !col3Occupied ? 2 : 1;
    nodes.push({
      id: 'officer',
      kind: 'profile',
      icon: UserIcon,
      title: enrolmentOfficers > 1 ? 'Enrolment Officers' : 'Enrolment Officer',
      subtitle: `Processes enrolments (${manualPct}% manual)`,
      cost: formatCostBadge(enrolmentOfficers * SALARIES.enrolmentOfficer),
      count: enrolmentOfficers,
      tone: 'traditional',
      col: salesLaneCol,
      colSpan: span,
      row: 4,
    });
    if (showSales) {
      edges.push({ from: 'sales', to: 'officer' });
    } else {
      // No sales — web (if visible) and phone both flow direct to
      // enrolment.
      if (emailVisible) edges.push({ from: 'c-web', to: 'officer' });
      if (hasIvr) {
        edges.push({ from: 'ivr', to: 'officer' });
      } else if (hasReceptionist) {
        edges.push({ from: 'reception', to: 'officer' });
      } else {
        edges.push({ from: 'c-phone', to: 'officer' });
      }
    }
    edges.push({ from: 'officer', to: 'ax' });
  }

  // Support Officer — always sits in col 3 (phone lane) directly under
  // whatever's sitting at the top of col 3 (IVR or Receptionist). Because
  // Receptionist gets pushed to col 4 whenever it would collide with the
  // IVR → Support line, col 3 always has a clear drop from IVR (or from
  // Receptionist when there's no IVR) down into Support at row 3.
  if (showSupport) {
    nodes.push({
      id: 'support',
      kind: 'profile',
      icon: HelpCircleIcon,
      title:
        supportOfficers > 1
          ? 'Student Support Officers'
          : 'Student Support Officer',
      subtitle: 'Student support, complex issues',
      cost: formatCostBadge(supportOfficers * SALARIES.supportOfficer),
      count: supportOfficers,
      tone: 'traditional',
      col: 3,
      row: 3,
    });
    if (hasIvr) {
      edges.push({ from: 'ivr', to: 'support' });
    } else if (hasReceptionist) {
      edges.push({ from: 'reception', to: 'support' });
    } else {
      // No phone router — phone feeds Support directly. Without this
      // edge the Support Officer would be an orphan (no incoming) and
      // get pruned from the layout, which hides a card the user
      // explicitly configured.
      edges.push({ from: 'c-phone', to: 'support' });
    }
    // When Support Officer is the ONLY enquiry handler (no sales, no
    // enrolment), email also drops to them — otherwise c-web would be
    // a channel with no downstream handler. In an RTO with only a
    // Support Officer, they're the catch-all for whatever comes in.
    if (!showSales && !showEnrolment && emailVisible) {
      edges.push({ from: 'c-web', to: 'support' });
    }
  }

  // Grid widens to 4 cols only when Receptionist needed to jump lanes to
  // clear the IVR → Support drop. Otherwise stays 3 cols.
  const totalCols = receptionToCol4 ? 4 : 3;

  nodes.push({
    id: 'ax',
    kind: 'destination',
    label: 'aXcelerate',
    col: 1,
    colSpan: totalCols,
    row: 5,
  });

  // Widen any handler whose direct-channel inputs don't drop straight
  // down into it. Shared with the Leagen builder via the same logic
  // inlined here (Traditional uses laneSize 1 so default colSpan is 1).
  widenHandlersForStraightDrops(nodes, edges, hiddenChannels, 1);

  // Annotate every → aXcelerate edge with its enrolment split share.
  // Manual paths collectively carry `manualPct`% of total enrolments;
  // auto paths carry the remaining `100 - manualPct`%. Each individual
  // edge gets the GROUP percentage so the reader can scan the chart
  // and see at a glance "X% of enrolments bypass humans entirely".
  annotateEnrolmentEdges(edges, manualPct, hiddenChannels);

  return finalizeLayout(
    { cols: totalCols, nodes, edges, totalCost, staffCount },
    hiddenChannels,
    1,
  );
}

// Label → aXcelerate edges with the manual / auto enrolment split.
// Runs on both Traditional and Leagen flows — the caller passes the
// flow-appropriate manualPct (Leagen side feeds a quartered value to
// reflect the AI absorbing most manual enrolments). When the chart
// has MULTIPLE auto paths (e.g. Leagen's axform + ai both feed ax),
// the `autoPct` is SHARED evenly between them so the labels sum back
// to the real auto share. Manual edges carry the full manualPct on
// each edge because visually there's usually one "Manually enrolled"
// line and the percentage refers to the overall share.
function annotateEnrolmentEdges(
  edges: Edge[],
  manualPct: number,
  hiddenNodeIds: Set<string> = new Set(),
) {
  const autoPct = Math.max(0, Math.min(100, 100 - manualPct));
  const manPct = Math.max(0, Math.min(100, manualPct));
  const AUTO_SOURCES = new Set(['axform', 'ai']);
  // Exclude auto edges whose source is hidden (e.g. axform is pruned
  // because the RTO doesn't have the online enrolment form). Without
  // this the split would keep counting the pruned source — "Auto
  // enrolled (48%)" on the one surviving edge when it should be the
  // full 95%.
  const isAutoEdge = (edge: Edge) =>
    edge.to === 'ax' &&
    !hiddenNodeIds.has(edge.from) &&
    (edge.tone === 'auto-enrolled' ||
      (edge.tone !== 'manually-enrolled' && AUTO_SOURCES.has(edge.from)));
  const autoCount = edges.filter(isAutoEdge).length;
  // Share the auto percentage across every auto edge. Round to the
  // nearest integer so the labels stay short ("Auto enrolled (47%)").
  // If only one auto edge exists, it gets the full autoPct.
  const autoShare = autoCount > 0 ? Math.round(autoPct / autoCount) : autoPct;
  for (const edge of edges) {
    if (edge.to !== 'ax') continue;
    const auto = isAutoEdge(edge);
    edge.label = auto
      ? `Auto enrolled (${autoShare}%)`
      : `Manually enrolled (${manPct}%)`;
  }
}

// Post-process step: for every profile handler with >=1 direct channel
// edge, widen/shift its col+colSpan so the handler's column range
// covers the union of its channel sources. Guarantees channel→handler
// edges render as straight verticals instead of diagonals. Leaves the
// AI, axform, and IVR system nodes alone because they're auto-handlers
// whose placement is driven by other concerns.
function widenHandlersForStraightDrops(
  nodes: FlowNode[],
  edges: Edge[],
  hiddenChannels: Set<string>,
  defaultSpan: number,
) {
  const surviving = new Set(
    nodes.filter((n) => !hiddenChannels.has(n.id)).map((n) => n.id),
  );
  const channelIds = new Set(
    nodes
      .filter((n) => n.kind === 'channel' && surviving.has(n.id))
      .map((n) => n.id),
  );
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    if (n.kind !== 'profile') continue;
    if (n.id === 'axform' || n.id === 'ai' || n.id === 'ivr') continue;
    const directChannels = edges
      .filter((e) => e.to === n.id && channelIds.has(e.from))
      .map((e) => nodes.find((x) => x.id === e.from))
      .filter((x): x is FlowNode => !!x);
    if (directChannels.length === 0) continue;
    const minCol = Math.min(...directChannels.map((c) => c.col));
    const maxCol = Math.max(
      ...directChannels.map((c) => c.col + (c.colSpan ?? defaultSpan) - 1),
    );
    const currentStart = n.col;
    const currentEnd = n.col + (n.colSpan ?? defaultSpan) - 1;
    if (currentStart <= minCol && currentEnd >= maxCol) continue;
    const newCol = Math.min(minCol, currentStart);
    const newEnd = Math.max(maxCol, currentEnd);
    nodes[i] = { ...n, col: newCol, colSpan: newEnd - newCol + 1 };
  }
}

// ---------- Leagen flow ----------
// Leagen side mirrors whatever structure the traditional side has — in
// combined mode, a single "Support Officer" card absorbs every human
// role; in separate mode, each traditional role appears halved (floored,
// min 1 if the traditional side had any) so the viewer can see which
// roles Leagen reduces and by how much. AI absorbs the bulk of the
// volume; the surviving humans only handle escalations + the residual
// phone traffic.

export type LeagenInputs = {
  phoneCalls: number;
  emailEnquiries: number;
  manualPct: number; // 0-100, same field as TraditionalInputs. Used to
  // compute the auto/manual enrolment split labels on the chart edges.
  hasOnlineForm: boolean;
  takesPhone: boolean;
  takesEmail: boolean;
  hasReceptionist: boolean;
  hasIvr: boolean;
  teamMode: TeamMode;
  combinedCount: number;
  enrolmentOfficers: number;
  salesOfficers: number;
  supportOfficers: number;
};

// Halve any traditional-side role count and floor to int. A role that
// existed at all on the traditional side sticks around on the Leagen
// side (even if n=1 halves to 0) so the viewer sees every role Leagen
// can replace — the bullet list below the chart covers the fact that
// Leagen can scale this down further as volume stabilises.
function halveRoleCount(n: number): number {
  if (n <= 0) return 0;
  return Math.max(1, Math.floor(n / 2));
}

export function buildLeagenFlow(inputs: LeagenInputs): FlowLayout & {
  humanCost: number;
  noHumans: boolean;
  staffCount: number;
} {
  const {
    phoneCalls,
    emailEnquiries,
    manualPct,
    hasOnlineForm,
    takesPhone,
    takesEmail,
    hasReceptionist,
    hasIvr,
    teamMode,
    combinedCount,
    enrolmentOfficers,
    salesOfficers,
    supportOfficers,
  } = inputs;

  const hiddenChannels = new Set<string>();
  if (!takesPhone || phoneCalls === 0) hiddenChannels.add('c-phone');
  if (!takesEmail || emailEnquiries === 0) hiddenChannels.add('c-email');
  if (!hasOnlineForm) {
    // Online form off → hide the whole c-form column + axform card.
    hiddenChannels.add('c-form');
    hiddenChannels.add('axform');
  }

  const useSeparate = teamMode === 'separate';
  // Halved role counts for the Leagen side.
  const leagenEnrolment = useSeparate ? halveRoleCount(enrolmentOfficers) : 0;
  const leagenSales = useSeparate ? halveRoleCount(salesOfficers) : 0;
  const leagenSupport = useSeparate ? halveRoleCount(supportOfficers) : 0;
  const hasAnyLeagenRole =
    leagenEnrolment > 0 || leagenSales > 0 || leagenSupport > 0;
  const combinedHumans = useSeparate ? 0 : halveRoleCount(combinedCount);
  // "No humans" = no officers AND no receptionist. In this state the AI
  // has no escalation endpoint, which violates ASQA's AI guidance and
  // Privacy Act APP 1.8 (right to human review of automated enrolment
  // decisions). We hide the email/phone channels (nowhere for them to
  // land) and signal the caller to render a compliance notice.
  const noHumans =
    !hasReceptionist &&
    ((useSeparate && !hasAnyLeagenRole) ||
      (!useSeparate && combinedHumans === 0));
  if (noHumans) {
    hiddenChannels.add('c-email');
    hiddenChannels.add('c-phone');
  }
  // Fallback "Admin Team" card only renders in COMBINED mode. Separate
  // mode never falls back to combinedCount — switching the team-mode
  // toggle should not carry the combined headcount into the separate
  // view. When separate mode has no officers AND no receptionist the
  // noHumans notice handles the empty state; with reception on but no
  // officers the escalation loop simply has no endpoint (reception is
  // the only human, not an AI escalation target).
  const showFallbackOverseer = !noHumans && !useSeparate;

  const humanCost = showFallbackOverseer
    ? halveRoleCount(combinedCount) * SALARIES.generalist +
      (hasReceptionist ? SALARIES.receptionist : 0) +
      (hasIvr ? IVR_ANNUAL_COST : 0)
    : leagenEnrolment * SALARIES.enrolmentOfficer +
      leagenSales * SALARIES.salesOfficer +
      leagenSupport * SALARIES.supportOfficer +
      (hasReceptionist ? SALARIES.receptionist : 0) +
      (hasIvr ? IVR_ANNUAL_COST : 0);

  const nodes: FlowNode[] = [
    {
      id: 'c-form',
      kind: 'channel',
      icon: FileTextIcon,
      label: 'Enrolment Form',
      col: 1,
      colSpan: 2,
      row: 1,
    },
    {
      id: 'c-web',
      kind: 'channel',
      icon: MessageCircleIcon,
      label: 'Chat Widget',
      col: 3,
      colSpan: 2,
      row: 1,
    },
    {
      id: 'c-email',
      kind: 'channel',
      icon: MailIcon,
      label: 'Email Enquiry',
      col: 5,
      colSpan: 2,
      row: 1,
      volume: `${emailEnquiries.toLocaleString()}/mo`,
    },
    {
      id: 'c-phone',
      kind: 'channel',
      icon: PhoneIcon,
      label: 'Phone Call',
      col: 7,
      colSpan: 2,
      row: 1,
      volume: `${phoneCalls.toLocaleString()}/mo`,
    },
    {
      id: 'axform',
      kind: 'profile',
      customIcon: <AxcelerateBrandIcon />,
      title: 'aXcelerate Online Enrolment Form',
      subtitle: 'Students self-enrol — no human touches it',
      tone: 'traditional',
      col: 1,
      colSpan: 2,
      row: 2,
    },
    {
      id: 'ai',
      kind: 'profile',
      customIcon: <LeagenBrandIcon />,
      title: 'Leagen AI Agent',
      subtitle: 'Handles enquiries, enrolments, follow-ups',
      tone: 'leagen',
      status: { tone: 'green', label: 'Unlimited 24/7 capacity' },
      col: 3,
      colSpan: 2,
      row: 2,
    },
  ];

  const edges: Edge[] = [
    { from: 'c-form', to: 'axform' },
    { from: 'axform', to: 'ax' },
    { from: 'c-web', to: 'ai' },
  ];

  // Channel visibility — used by the handler-placement logic below to
  // decide whether a handler needs to widen or move so channel→handler
  // edges drop straight down instead of diagonally.
  const leagenPhoneVisible = takesPhone && phoneCalls > 0;
  const leagenEmailVisible = takesEmail && emailEnquiries > 0;
  const leagenNoRouter = !hasIvr && !hasReceptionist;
  // When a handler receives c-phone directly (no router absorbing the
  // phone call first), it must sit in a column range that includes the
  // phone lane (col 7-8). Returns {col, span} for a handler that would
  // naturally sit in col 5-6: stays at col 5-6 when only email feeds
  // it, widens to col 5-8 when both email + phone feed it directly,
  // moves to col 7-8 when only phone feeds it directly.
  function handlerPlacement(feedsEmail: boolean, feedsPhoneDirect: boolean) {
    if (feedsPhoneDirect && feedsEmail) return { col: 5, colSpan: 4 };
    if (feedsPhoneDirect && !feedsEmail) return { col: 7, colSpan: 2 };
    return { col: 5, colSpan: 2 };
  }

  // ---------- Phone lane ----------
  // Phone traffic wants to drop straight down in col 7-8
  // (c-phone → IVR → Support Officer → reception). The lane-5 jump is
  // only necessary when ALL of IVR, Receptionist, Sales Support AND
  // Support Officer are present — because Support Officer's normal
  // col-5 home gets bumped to col 7-8 by Sales Support, at which point
  // col 7-8 row 3 is taken and Reception needs its own column. In
  // every other combination the phone lane can collapse back to 8 cols:
  //   - no IVR or no reception: no lane-5 anyway
  //   - IVR + reception but no support: col 7-8 row 3 is free, reception sits under IVR
  //   - IVR + reception + support but no sales: support stays at col 5-6, col 7-8 row 3 is free
  //   - combined/fallback mode: no separate support card, col 7-8 row 3 is free
  const receptionToLane5 =
    hasIvr && hasReceptionist && leagenSupport > 0 && leagenSales > 0;
  const leagenCols = receptionToLane5 ? 10 : 8;
  if (!noHumans && hasIvr) {
    nodes.push({
      id: 'ivr',
      kind: 'profile',
      icon: PhoneForwardedIcon,
      title: 'IVR Phone Routing',
      subtitle: buildIvrSubtitle({
        receptionist: hasReceptionist,
        enrolments: showFallbackOverseer || leagenSales > 0,
        support: !showFallbackOverseer && leagenSupport > 0,
      }),
      cost: formatCostBadge(IVR_ANNUAL_COST),
      costSub: '/yr',
      tone: 'system',
      col: 7,
      colSpan: 2,
      row: 2,
    });
    edges.push({ from: 'c-phone', to: 'ivr' });
  }
  // When reception is the only human on the Leagen side (no officers,
  // no combined-mode Admin Team), the receptionist has to cover both
  // the email and phone channels themselves — and do manual enrolments
  // on the back of those enquiries. Widen their card to span both
  // lanes (col 5-8) so email + phone both drop STRAIGHT DOWN into it.
  const receptionIsOnlyHuman =
    hasReceptionist && useSeparate && !hasAnyLeagenRole;
  if (hasReceptionist) {
    nodes.push({
      id: 'admin',
      kind: 'profile',
      icon: HeadsetIcon,
      title: 'Receptionist',
      subtitle: receptionIsOnlyHuman
        ? 'Answers enquiries + does manual enrolments'
        : 'Answers phone when students want a human',
      cost: formatCostBadge(SALARIES.receptionist),
      tone: 'leagen',
      meta: receptionIsOnlyHuman ? undefined : '~80% reduced volume',
      col: receptionIsOnlyHuman ? 5 : receptionToLane5 ? 9 : 7,
      colSpan: receptionIsOnlyHuman ? 4 : 2,
      // When reception is the only human, drop it to row 3 — same row as
      // Enrolment Officer in other scenarios. This makes AI → Reception
      // a true forward edge (row 2 → row 3) and Reception → AI a true
      // back-edge, so the two lines route through entirely different
      // paths (forward bezier vs back-lane) instead of colliding as
      // same-row back-edges.
      row: receptionIsOnlyHuman ? 3 : receptionToLane5 ? 3 : hasIvr ? 3 : 2,
    });
    if (hasIvr) {
      edges.push({ from: 'ivr', to: 'admin' });
    } else {
      edges.push({ from: 'c-phone', to: 'admin' });
    }
    // When reception is the only human, email drops directly into them
    // too (AI doesn't handle voice, and without officers reception is
    // the only enquiry handler for email).
    if (receptionIsOnlyHuman) {
      edges.push({ from: 'c-email', to: 'admin' });
    }
  }

  // ---------- Human handlers ----------
  // Lane plan on the Leagen side:
  //   col 1-2 — Enrolment Officer (under axform)
  //   col 3-4 — AI Support Agent (already placed at row 2)
  //   col 5-6 — Sales Support (under Email Enquiry channel)
  //   col 7-8 — Support Officer (under Phone Call channel)
  // Placing Support Officer in col 7-8 makes the phone lane a straight
  // vertical drop (channel → router → support). Sales Support at col 5-6
  // takes email + acts as the AI escalation endpoint. With Sales Support
  // at col 5-6 and AI at col 3-4, the escalated/resolved edges sit in
  // the col 4-5 gutter — no card crossings.
  //
  // The AI escalation + resolution loop connects to whichever card owns
  // the id "overseer": Support Officer in the fallback case, Sales
  // Support in the separate-role case.
  // Phone-lane handler row sits below whatever phone-router is present.
  // Since Receptionist jumps to lane 5 when IVR also exists, col 7-8
  // always has exactly one phone-router card above the Support Officer
  // (or zero). AI is always at row 2, so the handler must sit at row 3+
  // regardless of whether a router is present — otherwise AI→handler is
  // a sideways edge at row 2 (visually ambiguous and fails the matrix
  // "forward edges go strictly downward" check).
  const supportRow = 3;
  if (showFallbackOverseer) {
    // Combined mode (or no separate officers configured) — one human
    // card does every role, mirroring the Traditional-side "Admin Team"
    // / "Admin Generalist" pattern. The card uses id='overseer' so the
    // AI escalation/resolution edges still wire up to it correctly.
    // Halve the traditional combined headcount for the Leagen side
    // (same floor-to-min-1 rule as separate mode).
    const leagenCombined = halveRoleCount(combinedCount);
    const overseerPos = handlerPlacement(
      leagenEmailVisible,
      leagenNoRouter && leagenPhoneVisible,
    );
    nodes.push({
      id: 'overseer',
      kind: 'profile',
      icon: UsersIcon,
      title: leagenCombined > 1 ? 'Admin Team' : 'Admin Generalist',
      subtitle: 'Phones, enrolments, follow-ups, support',
      cost: formatCostBadge(leagenCombined * SALARIES.generalist),
      count: leagenCombined,
      tone: 'leagen',
      col: overseerPos.col,
      colSpan: overseerPos.colSpan,
      row: supportRow,
    });
    edges.push({ from: 'c-email', to: 'overseer' });
    if (hasIvr) {
      edges.push({ from: 'ivr', to: 'overseer' });
    } else if (hasReceptionist) {
      edges.push({ from: 'admin', to: 'overseer' });
    } else {
      edges.push({ from: 'c-phone', to: 'overseer' });
    }
  } else if (!noHumans) {
    // Sales Support sits in col 5-6 (under Email Enquiry). It owns the
    // "overseer" id so the AI escalated/resolved edges always find it.
    // When IVR exists we also route IVR → Sales Support (mirroring the
    // traditional-side "IVR press 2 enrolments" branch), so the three
    // IVR targets — Sales Support, Support Officer, Receptionist — all
    // sit at row 3, one per lane, and the IVR fan-out is three clean
    // forward-bezier drops with no card crossings.
    if (leagenSales > 0) {
      nodes.push({
        id: 'overseer',
        kind: 'profile',
        icon: UserPlusIcon,
        title: 'Sales Support',
        subtitle: 'Backs up AI on complex enquiries',
        cost: formatCostBadge(leagenSales * SALARIES.salesOfficer),
        count: leagenSales,
        tone: 'leagen',
        col: 5,
        colSpan: 2,
        row: 3,
      });
      // Sales Support receives email (it's the "complex enquiries"
      // handler — course questions, pricing, etc.) plus any phone
      // routing from IVR / Reception below.
      edges.push({ from: 'c-email', to: 'overseer' });
      if (hasIvr) {
        // IVR press 2 routes enrolment callers directly to Sales Support.
        edges.push({ from: 'ivr', to: 'overseer' });
      } else if (hasReceptionist) {
        // No IVR — Receptionist delegates course-interested callers to
        // Sales Support (mirroring the Traditional side's reception →
        // sales handoff when there's no IVR to auto-route them).
        edges.push({ from: 'admin', to: 'overseer' });
      }
    }
    if (leagenSupport > 0) {
      // Support Officer takes col 5-6 (the Email lane / "third column")
      // when there's no Sales Support above it — email can then drop
      // straight down into Support Officer without any diagonal. When
      // Sales Support IS present in col 5-6, Support Officer moves out
      // to col 7-8 so the phone-lane router in col 7-8 feeds straight
      // down into it instead.
      const supportCol = leagenSales > 0 ? 7 : 5;
      nodes.push({
        id: 'leagen-support',
        kind: 'profile',
        icon: HelpCircleIcon,
        title:
          leagenSupport > 1
            ? 'Student Support Officers'
            : 'Student Support Officer',
        subtitle: 'Student support, complex issues',
        cost: formatCostBadge(leagenSupport * SALARIES.supportOfficer),
        count: leagenSupport,
        tone: 'leagen',
        col: supportCol,
        colSpan: 2,
        row: supportRow,
      });
      // When Support Officer sits in the email lane (no Sales Support),
      // email drops straight down to them. When Sales Support has the
      // email lane, Support Officer receives email via Sales Support's
      // handoff below (not a duplicate c-email edge here).
      if (leagenSales === 0) {
        edges.push({ from: 'c-email', to: 'leagen-support' });
      }
      if (hasIvr) {
        edges.push({ from: 'ivr', to: 'leagen-support' });
      } else if (hasReceptionist) {
        edges.push({ from: 'admin', to: 'leagen-support' });
      } else {
        edges.push({ from: 'c-phone', to: 'leagen-support' });
      }
      // Support Officer does NOT enrol — they resolve tickets and end
      // the conversation. Only the Enrolment Officer card has the
      // "Manually enrolled" line into aXcelerate.
    } else if (hasAnyLeagenRole) {
      // No Support Officer card — the phone lane still needs a terminal
      // handler. Cascade: Sales Support → Enrolment Officer → AI. The
      // Enrolment Officer is a legitimate phone handler (they enrol
      // leads that call in) so we prefer them over handing a live phone
      // call straight to the AI agent. Skipped entirely when no
      // officers exist — reception-only is handled by the receptionist
      // doing manual enrolment (admin → ax edge added elsewhere).
      const phoneTarget =
        leagenSales > 0
          ? 'overseer'
          : leagenEnrolment > 0
            ? 'leagen-enrolment'
            : 'ai';
      if (hasIvr) {
        // IVR already routes to 'overseer' via the sales block above —
        // adding another ivr→overseer here would draw a duplicate
        // arrow on top of the first. Only add a fallback IVR→phoneTarget
        // edge when that target isn't already hit by one of IVR's
        // existing branches (reception/overseer/support).
        if (phoneTarget !== 'overseer' || leagenSales === 0) {
          edges.push({ from: 'ivr', to: phoneTarget });
        }
      } else if (hasReceptionist) {
        // Same de-dup rule for Reception — the sales block already
        // adds admin→overseer when sales exists, so don't push a
        // second identical edge here.
        if (phoneTarget !== 'overseer' || leagenSales === 0) {
          edges.push({ from: 'admin', to: phoneTarget });
        }
      } else {
        edges.push({ from: 'c-phone', to: phoneTarget });
      }
    }
    // Email always goes to the AI first (wired at the top of the flow).
    // When the AI escalates, the escalated/resolved edges below handle
    // the handoff to the human endpoint.
    if (leagenEnrolment > 0) {
      // Enrolment always sits in col 5-6 (the Email lane / "third
      // column"). When Sales Support exists at col 5-6 row 3, Enrolment
      // stacks directly beneath it at row 4 — a straight vertical
      // handoff. When Sales Support is absent (sales=0), Enrolment stays
      // in col 5-6 and receives from the AI agent via a short diagonal;
      // we do NOT pull it into col 3-4 under the AI because that puts
      // two card stacks in the same lane and forces later edges to
      // cross over the AI. The red "Manually enrolled" line into
      // aXcelerate always originates here.
      nodes.push({
        id: 'leagen-enrolment',
        kind: 'profile',
        icon: UserIcon,
        title: leagenEnrolment > 1 ? 'Enrolment Officers' : 'Enrolment Officer',
        subtitle: 'Handles residual manual enrolments',
        cost: formatCostBadge(leagenEnrolment * SALARIES.enrolmentOfficer),
        count: leagenEnrolment,
        tone: 'leagen',
        col: 5,
        colSpan: 2,
        row: leagenSales > 0 ? 4 : 3,
      });
      // Fed by Sales Support when present (mirroring the traditional
      // sales → enrolment handoff). When no Sales Support exists, the
      // AI → Enrolment connection is drawn later as the dashed
      // "Escalated" edge — we deliberately DO NOT add a second default
      // ai → enrolment edge here, because that would show a redundant
      // plain arrow alongside the escalated one (two lines saying the
      // same thing). The escalation edge alone keeps enrolment out of
      // the orphan-prune.
      if (leagenSales > 0) {
        edges.push({ from: 'overseer', to: 'leagen-enrolment' });
      } else if (leagenSupport === 0) {
        // No Sales Support AND no Support Officer — Enrolment Officer
        // is the only human endpoint, so email flows straight into it.
        // (When a Support Officer exists and takes the email lane, it
        // has already claimed c-email above — don't duplicate.)
        edges.push({ from: 'c-email', to: 'leagen-enrolment' });
      }
      edges.push({ from: 'leagen-enrolment', to: 'ax' });
    }
  }

  // AI escalation + resolution loop — drawn to whichever human card is
  // the primary escalation endpoint. Priority: Sales Support (overseer)
  // > Enrolment Officer > Receptionist > nothing. The Receptionist is
  // the last-resort endpoint when no officers exist at all — the AI
  // simply can't handle phone calls, so when only reception exists the
  // receptionist is the manual-enrolment handler.
  edges.push({ from: 'ai', to: 'ax' });
  const overseerPresent = nodes.some((n) => n.id === 'overseer');
  const enrolmentPresent = nodes.some((n) => n.id === 'leagen-enrolment');
  const supportPresent = nodes.some((n) => n.id === 'leagen-support');
  const receptionPresent = nodes.some((n) => n.id === 'admin');
  const receptionOnlyHuman =
    receptionPresent &&
    !overseerPresent &&
    !enrolmentPresent &&
    !supportPresent;
  // Escalation priority: Sales Support > Enrolment Officer > Support
  // Officer > reception-only. Support Officer is a legitimate escalation
  // target when it's the sole human handler (e.g. a Support-only RTO, or
  // Support + Receptionist with no officers on the enquiry side).
  const escalationEndpoint = overseerPresent
    ? 'overseer'
    : enrolmentPresent
      ? 'leagen-enrolment'
      : supportPresent
        ? 'leagen-support'
        : receptionOnlyHuman
          ? 'admin'
          : null;
  // When reception is the only human handler (no officers), the
  // receptionist does the manual enrolment themselves — add the
  // "Manually enrolled" line from reception to aXcelerate.
  if (receptionOnlyHuman) {
    edges.push({ from: 'admin', to: 'ax' });
  }
  if (escalationEndpoint) {
    edges.push({
      from: 'ai',
      to: escalationEndpoint,
      style: 'dashed',
      tone: 'escalated',
    });
    // Sales Support (overseer) doesn't enrol — it hands off to Enrolment
    // Officer which already has its own → ax edge. Only add overseer→ax
    // when there's no dedicated Enrolment Officer below. Enrolment
    // Officer always has its own → ax edge so we never add it twice.
    if (escalationEndpoint === 'overseer' && !enrolmentPresent) {
      edges.push({ from: 'overseer', to: 'ax' });
    }
    edges.push({
      from: escalationEndpoint,
      to: 'ai',
      tone: 'resolved',
    });
  }

  nodes.push({
    id: 'ax',
    kind: 'destination',
    label: 'aXcelerate',
    col: 1,
    colSpan: leagenCols,
    row: 4,
  });

  widenHandlersForStraightDrops(nodes, edges, hiddenChannels, 2);
  annotateEnrolmentEdges(edges, manualPct, hiddenChannels);

  // Leagen-side human head count — mirrors the Traditional builder's
  // staffCount so the comparison badge on the Leagen column can show
  // the NEW team size, not just "no new hires". Halved role counts
  // clamp to min-1 via halveRoleCount, so this value reflects the
  // realistic Leagen team (not the un-halved pre-normalisation values).
  const leagenStaffCount = showFallbackOverseer
    ? halveRoleCount(combinedCount) + (hasReceptionist ? 1 : 0)
    : leagenEnrolment + leagenSales + leagenSupport + (hasReceptionist ? 1 : 0);

  const finalized = finalizeLayout(
    { cols: leagenCols, nodes, edges, humanCost },
    hiddenChannels,
    2,
  );
  return { ...finalized, noHumans, staffCount: leagenStaffCount };
}

// ---------- Copy ----------

const _TRADITIONAL_WARNINGS = [
  'Phone busy during peak hours',
  'Staff unavailable — sick leave, holidays',
  'Response time: minutes to hours',
  'Follow-up rate: inconsistent',
  'Answer quality depends on who picks up',
];

const _LEAGEN_WINS = [
  '24/7 instant response',
  'Never sick, never on leave',
  '100% follow-up rate',
  'Consistent, high-quality answers',
  'Gets smarter with every interaction',
  'Scale headcount down further as AI absorbs more volume',
];

// ---------- UI helpers ----------

function SliderBlock(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  minLabel?: string;
  maxLabel?: string;
  format: (v: number) => string;
  disabled?: boolean;
}) {
  return (
    <div className={cn(props.disabled && 'opacity-50')}>
      <label className="text-muted-foreground text-sm font-medium">
        {props.label}
      </label>
      <p className="mt-1 text-3xl font-bold tracking-tight tabular-nums">
        {props.format(props.value)}
      </p>
      <Slider
        value={[props.value]}
        onValueChange={(v) => !props.disabled && props.onChange(v[0]!)}
        min={props.min}
        max={props.max}
        step={props.step}
        disabled={props.disabled}
        className={cn(
          'mt-4',
          props.disabled ? 'pointer-events-none' : 'cursor-pointer',
        )}
      />
      {(props.minLabel || props.maxLabel) && (
        <div className="text-muted-foreground mt-2 flex justify-between text-xs">
          <span>{props.minLabel ?? props.min}</span>
          <span>{props.maxLabel ?? props.max}</span>
        </div>
      )}
    </div>
  );
}

function IntakeToggle(props: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  // Compact = slightly smaller track + label, used by child toggles to
  // visually subordinate them to the parent row (aXcelerate Online
  // Enrolment Form nested under WordPress website).
  compact?: boolean;
}) {
  // Must NOT wrap the button in a <label>: a click on a button nested
  // inside a label bubbles to the label, which then implicitly re-clicks
  // the first labelable control — the button itself. That fires onClick
  // twice per user tap, netting zero state change.
  const effectiveChecked = !!props.checked && !props.disabled;
  const compact = !!props.compact;
  return (
    <div
      className={cn(
        'flex items-center gap-2.5',
        props.disabled && 'opacity-50',
      )}
    >
      <button
        type="button"
        role="switch"
        aria-checked={effectiveChecked}
        disabled={props.disabled}
        onClick={() => !props.disabled && props.onChange(!props.checked)}
        className={cn(
          'relative inline-flex shrink-0 items-center rounded-full transition-colors',
          compact ? 'h-4 w-7' : 'h-5 w-9',
          // Style guide: ON = emerald green, OFF = muted grey. Using
          // emerald-500 (not the theme primary) so the ON state reads
          // unambiguously green in both light and dark modes, and the
          // white thumb has enough contrast to be visible in either.
          effectiveChecked ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700',
          props.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        <span
          className={cn(
            'inline-block transform rounded-full bg-white shadow transition-transform',
            compact
              ? cn(
                  'h-3 w-3',
                  effectiveChecked ? 'translate-x-[14px]' : 'translate-x-[2px]',
                )
              : cn(
                  'h-4 w-4',
                  effectiveChecked ? 'translate-x-[18px]' : 'translate-x-[2px]',
                ),
          )}
        />
      </button>
      <span
        className={cn(
          'text-muted-foreground leading-tight font-medium',
          compact ? 'text-xs' : 'text-sm',
        )}
      >
        {props.label}
      </span>
    </div>
  );
}

function Stepper(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}) {
  const { label, value, onChange, min = 0, max = 20, disabled } = props;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3',
        disabled && 'opacity-50',
      )}
    >
      <span className="text-muted-foreground text-xs font-medium">{label}</span>
      <div className="border-border flex items-center gap-0.5 rounded-md border">
        <button
          type="button"
          disabled={disabled || value <= min}
          onClick={() => onChange(Math.max(min, value - 1))}
          className="hover:bg-muted flex h-7 w-7 cursor-pointer items-center justify-center text-sm disabled:cursor-not-allowed disabled:opacity-30"
        >
          −
        </button>
        <span className="min-w-[2ch] text-center text-sm font-bold tabular-nums">
          {value}
        </span>
        <button
          type="button"
          disabled={disabled || value >= max}
          onClick={() => onChange(Math.min(max, value + 1))}
          className="hover:bg-muted flex h-7 w-7 cursor-pointer items-center justify-center text-sm disabled:cursor-not-allowed disabled:opacity-30"
        >
          +
        </button>
      </div>
    </div>
  );
}

function _CostBar(props: {
  label: string;
  amount: string;
  suffix: string;
  barClassName: string;
  widthPct: number;
  tone: 'traditional' | 'leagen';
  footnote?: string;
}) {
  return (
    <div className="mt-6">
      <div className="mb-1.5 flex items-end justify-between">
        <span className="text-muted-foreground text-xs font-medium">
          {props.label}
        </span>
        <span
          className={cn(
            'text-2xl font-bold',
            props.tone === 'leagen' ? 'text-primary' : 'text-foreground',
          )}
        >
          {props.amount}
          <span className="text-muted-foreground text-sm font-normal">
            {props.suffix}
          </span>
        </span>
      </div>
      <div className="bg-muted h-4 w-full overflow-hidden rounded-full">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            props.barClassName,
          )}
          style={{ width: `${props.widthPct}%` }}
        />
      </div>
      {props.footnote && (
        <p className="text-muted-foreground mt-2 text-xs">{props.footnote}</p>
      )}
    </div>
  );
}

// Team composition block — renders above each flowchart so the
// reader sees at a glance who's handling enquiries on each side.
// Traditional shows "N humans · Mon-Fri 9-5". Leagen adds a second
// line for the AI: "1 AI agent · Unlimited 24/7", plus a (-M)
// reduction delta when Leagen's team is smaller than Traditional's.
function TeamComposition(props: {
  tone: 'traditional' | 'leagen';
  humans: number;
  humansDelta?: number;
  hasAi?: boolean;
}) {
  const { tone, humans, humansDelta = 0, hasAi = false } = props;
  const accent = tone === 'leagen' ? 'text-primary' : 'text-foreground';
  // Single-line composition string. For Leagen we append "+ Leagen AI
  // · Unlimited 24/7" after the human roster. Deliberately NOT
  // "1 AI agent" — that phrasing triggered "one AI = one person"
  // mental math. "Unlimited" captures the real capability (one logical
  // agent, unlimited concurrent instances) without the 1:1 comparison.
  // Single-line composition. For Leagen we collapse "Mon-Fri 9-5 + Leagen
  // AI · Unlimited 24/7" into the shorter "+ Leagen AI · 24/7" so the
  // whole line fits without wrapping — the AI's 24/7 coverage implicitly
  // replaces the Mon-Fri window on the human side.
  return (
    <div className="mt-3 flex flex-nowrap items-center gap-x-2 overflow-hidden text-sm whitespace-nowrap">
      <UsersIcon className="text-muted-foreground h-4 w-4 shrink-0" />
      <span className={cn('font-semibold tabular-nums', accent)}>
        {humans} {humans === 1 ? 'human' : 'humans'}
      </span>
      {humansDelta > 0 && (
        <span className="text-muted-foreground">(-{humansDelta})</span>
      )}
      {!hasAi && <span className="text-muted-foreground">· Mon-Fri 9-5</span>}
      {hasAi && (
        <>
          <span className="text-muted-foreground">+</span>
          <span className="h-4 w-4 shrink-0">
            <LeagenBrandIcon />
          </span>
          <span className={cn('font-semibold', accent)}>Leagen AI</span>
          <span className="text-muted-foreground">· 24/7</span>
        </>
      )}
    </div>
  );
}

// ---------- Outcome footer ----------
// Single-column consolidated comparison that sits below the two-column
// body (Traditional vs Leagen flowcharts). Replaces the per-column
// annual-cost card and warning/win bullets. Each row is a stacked bar
// pair — Traditional on top, Leagen below — with a short descriptor on
// the right explaining what each side looks like in practice.

// Palette for the outcome footer bars. Traditional is a dull emerald
// so the chart reads as a monochrome "intensity" story (the brighter
// the bar, the better the result). Leagen is the brand-green #0ac251.
// The shimmer gradient uses a LIGHT mid-stop (#a9ffc7) so the sweep
// reads as an obvious lighter band travelling through the bar rather
// than a subtle tint shift you'd have to squint to see.
const TRADITIONAL_GREEN = '#d6e0e7';
const LEAGEN_GREEN = '#5b3df5';
const LEAGEN_GRADIENT =
  'linear-gradient(90deg, #99e0ff 0%, #c4edff 50%, #99e0ff 100%)';

function OutcomeBar(props: {
  label: string;
  tradValue: string;
  tradPct: number; // raw 0-100 bar width for traditional
  tradCopy?: string;
  leagenValue: string;
  leagenPct: number; // raw 0-100 bar width for leagen
  leagenCopy?: string;
  delta?: string; // e.g. "50% less" or "Save A$55k/yr"
  // Gray subtitle under the delta that explains WHY the number
  // changes. Matches CapacityBar's "up to ~400/mo" supporting line.
  // Only used for reduction rows where tradCopy/leagenCopy are absent.
  deltaSubtitle?: string;
}) {
  const {
    label,
    tradValue,
    tradPct: rawTradPct,
    tradCopy,
    leagenValue,
    leagenPct: rawLeagenPct,
    leagenCopy,
    delta,
    deltaSubtitle,
  } = props;
  // Normalise so the longer pill ALWAYS reaches 100% of the track.
  // Without this, rows like Answer Quality (leagen=95, trad=50) render
  // shorter than rows where leagen=100, so the chart looks visually
  // inconsistent. Scaling both sides by 100/max keeps the ratio
  // between them (e.g. 95:50 stays 100:~52.6) while guaranteeing
  // every bar fills the track to the same right edge.
  const maxPct = Math.max(rawTradPct, rawLeagenPct, 1);
  const tradPct = (rawTradPct / maxPct) * 100;
  const leagenPct = (rawLeagenPct / maxPct) * 100;
  // Offset overlay layout. The track is always the same full width
  // across every metric row (same 100% reference), and inside it the
  // two bars are rendered as overlapping rounded pills:
  //
  //   - Traditional sits flush with the track's top-left corner at
  //     width = tradPct%. Dull emerald so it reads as the baseline.
  //   - Leagen floats ON TOP offset 5px right and 5px down at width =
  //     leagenPct%. Brand-green with shimmer.
  //
  // The offset lets the reader see a slice of Traditional peek out
  // behind Leagen even when the two widths are identical. Both bars
  // have independent rounded corners on all four sides (no clipping
  // via a shared overflow-hidden parent) so the overlay actually
  // reads as a separate floating pill, not a clipped segment.
  const BAR_HEIGHT = 20;
  const OFFSET = 5;
  const containerHeight = BAR_HEIGHT + OFFSET;
  // Two caption shapes depending on the metric type:
  //   - Qualitative rows supply tradCopy + leagenCopy → render two
  //     stacked dot-prefix lines so the reader can tell at a glance
  //     which line is the "old way" (dull-green dot) and which is the
  //     "new way" (bright-green dot), even without a top-right delta.
  //   - Quantitative / cost rows supply a delta + deltaSubtitle → the
  //     delta sits top-right (like CapacityBar's "2×") and the gray
  //     subtitle sits right-of-bar (like CapacityBar's "up to ~400/mo").
  const hasDotLines = !!(tradCopy && leagenCopy);
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm font-medium">{label}</span>
        {delta && (
          <span className="text-base font-bold" style={{ color: '#173b50' }}>
            {delta}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1" style={{ height: containerHeight }}>
          {/*
            Stacking order: whichever pill is SHORTER sits on top, so
            its value label is never hidden behind the other pill. For
            reductions (Leagen < Traditional): Leagen on top. For
            improvements (Traditional < Leagen): Traditional on top.
            Equal widths: Leagen on top so the animation catches the eye.
          */}
          {(() => {
            const tradOnTop = tradPct < leagenPct;
            const traditional = (
              <div
                key="trad"
                className="absolute top-0 left-0 flex items-center justify-end rounded-full pr-3"
                style={{
                  width: `${Math.max(4, tradPct)}%`,
                  height: BAR_HEIGHT,
                  backgroundColor: TRADITIONAL_GREEN,
                  zIndex: tradOnTop ? 2 : 1,
                }}
              >
                {tradPct > 14 && (
                  <span
                    className="text-[11px] font-semibold tabular-nums"
                    style={{ color: '#173b50' }}
                  >
                    {tradValue}
                  </span>
                )}
              </div>
            );
            const leagen = (
              <div
                key="leagen"
                className="absolute flex items-center justify-end rounded-full pr-3 shadow-[0_2px_8px_rgba(133,112,180,0.18)]"
                style={{
                  left: OFFSET,
                  top: OFFSET,
                  width: `calc(${Math.max(4, leagenPct)}% - ${OFFSET}px)`,
                  height: BAR_HEIGHT,
                  background: LEAGEN_GRADIENT,
                  backgroundSize: '200% 100%',
                  animation: 'ui-shimmer 2s linear infinite',
                  zIndex: tradOnTop ? 1 : 2,
                }}
              >
                {leagenPct > 14 && (
                  <span
                    className="text-[11px] font-bold tabular-nums"
                    style={{ color: '#173b50' }}
                  >
                    {leagenValue}
                  </span>
                )}
              </div>
            );
            return (
              <>
                {traditional}
                {leagen}
              </>
            );
          })()}
        </div>
        <div className="text-muted-foreground w-56 shrink-0 text-xs">
          {hasDotLines ? (
            <div className="space-y-1">
              <div className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: TRADITIONAL_GREEN }}
                />
                <span>{tradCopy}</span>
              </div>
              <div className="flex items-start gap-2">
                <span
                  aria-hidden
                  className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: '#99e0ff' }}
                />
                <span>{leagenCopy}</span>
              </div>
            </div>
          ) : deltaSubtitle ? (
            <span className="block text-right">{deltaSubtitle}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Small colour-dot + label pair used in the top-left legend. Semantic
// labels stay at the top of the footer so every row below can drop the
// Traditional/Leagen repeat without losing the reader's place.
function LegendDot(props: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="inline-block h-3 w-3 rounded-full"
        style={{ backgroundColor: props.color }}
      />
      <span className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
        {props.label}
      </span>
    </div>
  );
}

// One breakdown line inside the revenue calculator. Shows a label +
// the math that produces it (gray, small) on the left and the dollar
// contribution on the right. Keeps the calculation auditable — the
// board can see exactly how we got to the headline number.
function RevenueRow(props: { label: string; formula: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div>
        <div className="font-medium">{props.label}</div>
        <div className="text-muted-foreground mt-0.5 text-xs">
          {props.formula}
        </div>
      </div>
      <div className="shrink-0 text-right font-semibold tabular-nums">
        {props.value}
      </div>
    </div>
  );
}

// Capacity card — replaces the earlier bar-based CapacityBar. A bar
// didn't communicate scale for multipliers like "2×" or "∞" (the bar
// length was arbitrary relative to the number); a card with a big
// headline multiplier does the job more directly. Four of these sit
// in a 4×1 grid on desktop, 2×2 on tablet, stacked on mobile.
function CapacityCard(props: {
  label: string;
  multiplier: string; // e.g. "2×", "4×", "∞"
  detail: string; // e.g. "up to ~400 calls/mo"
}) {
  return (
    <div className="border-border/40 bg-muted/10 rounded-2xl border p-5">
      <div
        className="text-3xl font-bold tabular-nums"
        style={{ color: LEAGEN_GREEN }}
      >
        {props.multiplier}
      </div>
      <div className="mt-2 text-sm font-medium">{props.label}</div>
      <div className="text-muted-foreground mt-1 text-xs leading-snug">
        {props.detail}
      </div>
    </div>
  );
}

function OutcomeFooter(props: {
  view: number;
  traditional: ReturnType<typeof buildTraditionalFlow>;
  leagen: ReturnType<typeof buildLeagenFlow>;
  phoneCalls: number;
  emailEnquiries: number;
  manualPct: number;
  takesPhone: boolean;
  takesEmail: boolean;
  hasOnlineForm: boolean;
  monthlyEnrolments: number;
  avgCoursePrice: number;
}) {
  const {
    traditional,
    leagen,
    phoneCalls,
    emailEnquiries,
    manualPct,
    takesPhone,
    takesEmail,
    monthlyEnrolments,
    avgCoursePrice,
  } = props;

  // The BrightSegment's shimmer animation relies on a global @keyframes
  // `ui-shimmer` that slides background-position from 200% → -200%.
  // Injecting the keyframes here (the top-level container) guarantees
  // they're in the DOM before any segment mounts — otherwise the inline
  // `animation: 'ui-shimmer ...'` would reference an undefined keyframe
  // and the bars would render statically.
  useInjectKeyframes(
    'ui-shimmer-keyframes',
    '@keyframes ui-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }',
  );

  // Enrolment growth multiplier — scales with team size rather than a
  // flat "4×" across every RTO. Rationale: a single-person shop with
  // Leagen gets doubled because the same one human can now shepherd
  // twice as many enrolments through with AI handling discovery and
  // FAQ. A bigger team compounds the gain non-linearly because each
  // person's freed hours add up AND the team can specialise (some
  // focus on complex closes, others on volume follow-up). Formula is
  // a rounded log₂ on top of a conservative 2× floor:
  //   1 staff → 2×
  //   2 staff → 3×
  //   4 staff → 4×
  //   8 staff → 5×
  //   16 staff → 6×
  // Matches Dogan's "exponential at scale" intuition while staying
  // believable at the small end.
  const staffCount = Math.max(1, traditional.staffCount || 1);
  const enrolmentMultiplier = Math.max(
    2,
    Math.round(2 + Math.log2(staffCount)),
  );

  // Volume metrics — use the same reduction factors the builders use
  // so the footer stays in lock-step with the chart annotations.
  const effectivePhone = takesPhone ? phoneCalls : 0;
  const effectiveEmail = takesEmail ? emailEnquiries : 0;
  const leagenPhone = Math.round(effectivePhone * (1 - LEAGEN_PHONE_REDUCTION));
  const leagenEmail = Math.round(effectiveEmail * (1 - LEAGEN_EMAIL_REDUCTION));
  const leagenManual = Math.round(manualPct * (1 - LEAGEN_MANUAL_REDUCTION));

  // Cost savings
  const savings = Math.max(0, traditional.totalCost - leagen.humanCost);
  const costPctLeagen = Math.max(
    6,
    Math.round((leagen.humanCost / Math.max(1, traditional.totalCost)) * 100),
  );

  // Growth-capacity multipliers — derived from the reduction factors.
  // Phone: 50% reduction → team absorbs 2× the raw traffic.
  // Email: 75% reduction → 4× raw traffic.
  // Manual: 75% reduction → 4× enrolment throughput with the same
  //   manual-touch capacity.
  return (
    <div className="border-t px-6 py-12 md:px-12">
      <div className="mx-auto max-w-3xl">
        <div hidden={props.view !== 1}>
          <h3 className="text-center text-sm font-semibold tracking-wider uppercase">
            The Outcome
          </h3>
          <p className="text-muted-foreground mx-auto mt-2 max-w-xl text-center text-sm">
            Same team. Same infrastructure. Here&apos;s what Leagen changes.
          </p>

          {/* Legend — every bar row below follows this colour order:
            top bar = Traditional (dull green), bottom bar = Leagen
            (bright green, animated). Keeping the labels here means the
            rows themselves don't need to repeat "Traditional / Leagen"
            on every single metric. */}
          <div className="mt-8 flex items-center justify-start gap-6">
            <LegendDot color={TRADITIONAL_GREEN} label="Traditional" />
            <LegendDot color="#99e0ff" label="Leagen" />
          </div>

          {/* Quantitative reductions */}
          <div className="mt-6 space-y-6">
            <OutcomeBar
              label="Phone calls"
              tradValue={`${phoneCalls}/mo`}
              tradPct={100}
              leagenValue={`${leagenPhone}/mo`}
              leagenPct={100 * (1 - LEAGEN_PHONE_REDUCTION)}
              delta="50% less"
              deltaSubtitle="Chatbot absorbs FAQs before the caller picks up the phone"
            />
            <OutcomeBar
              label="Email enquiries"
              tradValue={`${emailEnquiries}/mo`}
              tradPct={100}
              leagenValue={`${leagenEmail}/mo`}
              leagenPct={100 * (1 - LEAGEN_EMAIL_REDUCTION)}
              delta="75% less"
              deltaSubtitle="AI answers most enquiries instantly — only the complex ones reach a human"
            />
            <OutcomeBar
              label="Manual enrolments"
              tradValue={`${manualPct}%`}
              tradPct={100}
              leagenValue={`${leagenManual}%`}
              leagenPct={100 * (1 - LEAGEN_MANUAL_REDUCTION)}
              delta="75% less"
              deltaSubtitle="Students self-serve via AI — very few still need a human to click enrol"
            />
          </div>

          {/* Qualitative reductions */}
          <div className="mt-10 space-y-6">
            <OutcomeBar
              label="Availability"
              tradValue="~60%"
              tradPct={60}
              tradCopy="Business hours only — sick leave, holidays"
              leagenValue="100%"
              leagenPct={100}
              leagenCopy="24/7 — never sick, never on leave"
            />
            <OutcomeBar
              label="Response time"
              tradValue="Slow"
              tradPct={25}
              tradCopy="Minutes to hours"
              leagenValue="Instant"
              leagenPct={100}
              leagenCopy="Answered the moment it's asked"
            />
            <OutcomeBar
              label="Follow-up rate"
              tradValue="~40%"
              tradPct={40}
              tradCopy="Inconsistent — depends on workload"
              leagenValue="100%"
              leagenPct={100}
              leagenCopy="Every enquiry followed up, every time"
            />
            <OutcomeBar
              label="Answer quality"
              tradValue="Variable"
              tradPct={50}
              tradCopy="Depends on who picks up"
              leagenValue="High"
              leagenPct={95}
              leagenCopy="Consistent — and improves with every interaction"
            />
          </div>

          {/* Annual cost with savings */}
          <div className="mt-10">
            <OutcomeBar
              label="Annual cost"
              tradValue={`${formatCurrency(traditional.totalCost)}/yr`}
              tradPct={100}
              leagenValue={`${formatCurrency(leagen.humanCost)}/yr`}
              leagenPct={costPctLeagen}
              delta={`Save ${formatCurrency(savings)}/yr`}
              deltaSubtitle={
                traditional.staffCount > leagen.staffCount
                  ? `Team reduced by ${
                      traditional.staffCount - leagen.staffCount
                    } — Leagen agent fee on top, join the waitlist for pricing`
                  : 'Leagen agent fee on top — join the waitlist for pricing'
              }
            />
          </div>
        </div>
        <div hidden={props.view !== 2}>
          {/* Growth capacity */}
          <div className="pt-2">
            <h4 className="text-center text-sm font-semibold tracking-wider uppercase">
              Potential Growth Capacity
            </h4>
            <p className="text-muted-foreground mx-auto mt-2 max-w-xl text-center text-sm">
              AI takes the noise off your team&apos;s plate. Here&apos;s what
              they can do with the time and focus it gives back.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <CapacityCard
                label="Chat / AI"
                multiplier="∞"
                detail="Unlimited enquiries — AI scales on its own, 24/7"
              />
              <CapacityCard
                label="Enrolments"
                multiplier={`${enrolmentMultiplier}×`}
                detail={`Scales with your ${staffCount}-person team via the automated enrolment path`}
              />
              <CapacityCard
                label="Phone time freed"
                multiplier={`~${Math.round(
                  (effectivePhone * LEAGEN_PHONE_REDUCTION * AVG_PHONE_MIN) /
                    60,
                )} hrs/mo`}
                detail="For complex calls, warm-lead callbacks, outbound follow-up"
              />
              <CapacityCard
                label="Email time freed"
                multiplier={`~${Math.round(
                  (effectiveEmail * LEAGEN_EMAIL_REDUCTION * AVG_EMAIL_MIN) /
                    60,
                )} hrs/mo`}
                detail="Hit SLAs, write thoughtful replies, proactive outreach"
              />
            </div>

            {/*
            Revenue calculator — turns the four capacity claims into a
            concrete monthly dollar figure the RTO can put in front of
            its board. Two input levers already captured on the intake
            form feed this:
              - monthlyEnrolments × avgCoursePrice → current sales
              - ENROLMENT_GROWTH_HEADROOM × current sales → enrolment opportunity
              - hours_freed × hourly_rate × employee_revenue_multiplier
                → revenue value of freed staff time
            All constants are declared inline so the math is audible on
            hover rather than hidden in config.
          */}
            {(() => {
              const STANDARD_HOURLY_RATE = 30; // A$ / hr — cost of the staff hour
              const EMPLOYEE_REVENUE_MULTIPLIER = 3; // healthy employee generates 3× their hourly cost in revenue
              // Headroom = (capacity - 1). An enrolmentMultiplier of 2 means
              // the team can do 1 extra enrolment for every current one;
              // a multiplier of 6 means 5× more than current. Scales with
              // staffCount so a bigger team gets a bigger headroom number.
              const ENROLMENT_GROWTH_HEADROOM = Math.max(
                0,
                enrolmentMultiplier - 1,
              );
              const hoursFreedPhone =
                (effectivePhone * LEAGEN_PHONE_REDUCTION * AVG_PHONE_MIN) / 60;
              const hoursFreedEmail =
                (effectiveEmail * LEAGEN_EMAIL_REDUCTION * AVG_EMAIL_MIN) / 60;
              const hoursFreedTotal = hoursFreedPhone + hoursFreedEmail;
              const enrolmentRevenue =
                ENROLMENT_GROWTH_HEADROOM * monthlyEnrolments * avgCoursePrice;
              const freedTimeCostSavings =
                hoursFreedTotal * STANDARD_HOURLY_RATE;
              const freedTimeRevenuePotential =
                freedTimeCostSavings * EMPLOYEE_REVENUE_MULTIPLIER;
              const totalGrowth = enrolmentRevenue + freedTimeRevenuePotential;
              return (
                <div className="border-border/40 bg-muted/10 mt-10 rounded-2xl border p-6">
                  <div className="flex flex-col items-start gap-4 md:flex-row md:items-baseline md:justify-between">
                    <div>
                      <h5 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                        Potential monthly growth revenue
                      </h5>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Based on your {monthlyEnrolments.toLocaleString()}{' '}
                        enrolments/mo at A$
                        {avgCoursePrice.toLocaleString()} average course price.
                      </p>
                    </div>
                    <div
                      className="text-4xl font-bold tabular-nums"
                      style={{ color: LEAGEN_GREEN }}
                    >
                      {formatCurrency(totalGrowth)}
                      <span className="text-muted-foreground ml-1 text-base font-normal">
                        /mo
                      </span>
                    </div>
                  </div>
                  <div className="mt-5 space-y-3 text-sm">
                    <RevenueRow
                      label="Enrolment revenue headroom"
                      formula={`${enrolmentMultiplier}× capacity on your ${staffCount}-person team → ${ENROLMENT_GROWTH_HEADROOM}× ${monthlyEnrolments.toLocaleString()} extra enrolments × A$${avgCoursePrice.toLocaleString()}`}
                      value={formatCurrency(enrolmentRevenue)}
                    />
                    <RevenueRow
                      label="Freed-time revenue potential"
                      formula={`~${Math.round(
                        hoursFreedTotal,
                      )} hrs/mo × A$${STANDARD_HOURLY_RATE}/hr × ${EMPLOYEE_REVENUE_MULTIPLIER}× revenue multiplier`}
                      value={formatCurrency(freedTimeRevenuePotential)}
                    />
                  </div>
                  <div className="border-border/40 mt-4 border-t pt-3 text-xs">
                    <div className="flex items-baseline justify-between">
                      <span className="text-muted-foreground">
                        Plus direct staff-time cost savings
                      </span>
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(freedTimeCostSavings)}/mo
                      </span>
                    </div>
                    <p className="text-muted-foreground/70 mt-1 text-[11px] italic">
                      ~{Math.round(hoursFreedTotal)} hrs/mo × A$
                      {STANDARD_HOURLY_RATE}/hr — the &quot;what you&apos;d pay
                      someone else to do this&quot; rate. Revenue potential
                      above uses the 3× multiplier most businesses target for
                      each employee hour.
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Extra bullets */}
          <div className="mt-12 space-y-2 border-t pt-8 text-sm">
            <div className="text-primary/80 flex items-start gap-2">
              <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Gets smarter with every interaction</span>
            </div>
            <div className="text-primary/80 flex items-start gap-2">
              <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Scale headcount down further as AI absorbs more volume
              </span>
            </div>
            <div className="text-primary/80 flex items-start gap-2">
              <CheckCircle2Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Your team swaps triage work for growth, curriculum, and the
                students who actually need a human.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Main component ----------

export function RoiCalculator() {
  const [step, setStep] = useState(0);
  const calculatorTop = useRef<HTMLDivElement>(null);
  const steps = ['Calculator', 'Outcome', 'Growth capacity'];
  const goToStep = (next: number) => {
    setStep(next);
    calculatorTop.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  };

  // Volumes
  const [monthlyEnrolments, setMonthlyEnrolments] = useState(100);
  const [onlinePct, setOnlinePct] = useState(80);
  const [phoneCalls, setPhoneCalls] = useState(200);
  const [emailEnquiries, setEmailEnquiries] = useState(100);
  // Avg course fee — drives the Growth Capacity revenue calculator.
  // Conservative default $500 per enrolment (short courses); slider
  // ranges from $100 up to $5,000 to cover Cert IV / Diploma pricing.
  const [avgCoursePrice, setAvgCoursePrice] = useState(500);

  // Channel toggles
  const [hasWordpress, setHasWordpress] = useState(true);
  const [hasOnlineFormRaw, setHasOnlineForm] = useState(true);
  // The aXcelerate online enrolment form is a WordPress-only widget —
  // it can only be embedded on a WordPress site. When the RTO doesn't
  // have a WordPress site, the form simply doesn't exist for them.
  const hasOnlineForm = hasWordpress && hasOnlineFormRaw;
  const [takesPhone, setTakesPhone] = useState(true);
  const [takesEmail, setTakesEmail] = useState(true);

  // Team structure
  const [teamMode, setTeamMode] = useState<TeamMode>('separate');
  const [combinedCount, setCombinedCount] = useState(1);
  const [enrolmentOfficers, setEnrolmentOfficers] = useState(1);
  const [salesOfficers, setSalesOfficers] = useState(0);
  const [supportOfficers, setSupportOfficers] = useState(0);

  // Infrastructure
  const [hasReceptionist, setHasReceptionist] = useState(true);
  const [hasIvr, setHasIvr] = useState(false);

  // View mode
  const [mode, setMode] = useState<'detailed' | 'normal'>('detailed');

  // Calculator state is persisted to localStorage so reloading the
  // page doesn't wipe the numbers someone just tuned. Key is versioned
  // so a future shape change can invalidate stale blobs without
  // crashing the parse. Hydration runs in a synchronous useLayoutEffect
  // (not a useEffect) so the saved values are swapped in BEFORE the
  // first paint — otherwise the user sees a jarring default-state
  // flash for the few hundred ms it takes useEffect to run. Paint is
  // gated on `hydrated` so the component renders nothing until the
  // effect has completed.
  const STORAGE_KEY = 'leagen-roi-calculator-v1';
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw) as Record<string, unknown>;
        if (typeof s.monthlyEnrolments === 'number')
          setMonthlyEnrolments(s.monthlyEnrolments);
        if (typeof s.onlinePct === 'number') setOnlinePct(s.onlinePct);
        if (typeof s.phoneCalls === 'number') setPhoneCalls(s.phoneCalls);
        if (typeof s.emailEnquiries === 'number')
          setEmailEnquiries(s.emailEnquiries);
        if (typeof s.avgCoursePrice === 'number')
          setAvgCoursePrice(s.avgCoursePrice);
        if (typeof s.hasWordpress === 'boolean')
          setHasWordpress(s.hasWordpress);
        if (typeof s.hasOnlineFormRaw === 'boolean')
          setHasOnlineForm(s.hasOnlineFormRaw);
        if (typeof s.takesPhone === 'boolean') setTakesPhone(s.takesPhone);
        if (typeof s.takesEmail === 'boolean') setTakesEmail(s.takesEmail);
        if (s.teamMode === 'combined' || s.teamMode === 'separate')
          setTeamMode(s.teamMode);
        if (typeof s.combinedCount === 'number')
          setCombinedCount(s.combinedCount);
        if (typeof s.enrolmentOfficers === 'number')
          setEnrolmentOfficers(s.enrolmentOfficers);
        if (typeof s.salesOfficers === 'number')
          setSalesOfficers(s.salesOfficers);
        if (typeof s.supportOfficers === 'number')
          setSupportOfficers(s.supportOfficers);
        if (typeof s.hasReceptionist === 'boolean')
          setHasReceptionist(s.hasReceptionist);
        if (typeof s.hasIvr === 'boolean') setHasIvr(s.hasIvr);
        if (s.mode === 'detailed' || s.mode === 'normal') setMode(s.mode);
      }
    } catch {
      // Stale / corrupt blob — safe to ignore, defaults stay in place.
    }
    setHydrated(true);
  }, []);
  useEffect(() => {
    // Don't write back defaults BEFORE the load effect has had a chance
    // to run — otherwise a fresh mount would overwrite an existing
    // saved blob with the initial defaults.
    if (!hydrated) return;
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          monthlyEnrolments,
          onlinePct,
          phoneCalls,
          emailEnquiries,
          avgCoursePrice,
          hasWordpress,
          hasOnlineFormRaw,
          takesPhone,
          takesEmail,
          teamMode,
          combinedCount,
          enrolmentOfficers,
          salesOfficers,
          supportOfficers,
          hasReceptionist,
          hasIvr,
          mode,
        }),
      );
    } catch {
      // Quota exceeded or private mode — silently ignore.
    }
  }, [
    hydrated,
    monthlyEnrolments,
    onlinePct,
    phoneCalls,
    emailEnquiries,
    avgCoursePrice,
    hasWordpress,
    hasOnlineFormRaw,
    takesPhone,
    takesEmail,
    teamMode,
    combinedCount,
    enrolmentOfficers,
    salesOfficers,
    supportOfficers,
    hasReceptionist,
    hasIvr,
    mode,
  ]);

  const effectiveOnlinePct = hasOnlineForm ? onlinePct : 0;
  const effectivePhoneCalls = takesPhone ? phoneCalls : 0;
  const effectiveEmails = takesEmail ? emailEnquiries : 0;
  const manualPct = 100 - effectiveOnlinePct;

  const traditional = useMemo(
    () =>
      buildTraditionalFlow({
        monthlyEnrolments,
        manualPct,
        phoneCalls: effectivePhoneCalls,
        emailEnquiries: effectiveEmails,
        hasOnlineForm,
        takesPhone,
        takesEmail,
        hasReceptionist,
        hasIvr,
        teamMode,
        combinedCount,
        enrolmentOfficers,
        salesOfficers,
        supportOfficers,
      }),
    [
      monthlyEnrolments,
      manualPct,
      effectivePhoneCalls,
      effectiveEmails,
      hasOnlineForm,
      takesPhone,
      takesEmail,
      hasReceptionist,
      hasIvr,
      teamMode,
      combinedCount,
      enrolmentOfficers,
      salesOfficers,
      supportOfficers,
    ],
  );
  const leagen = useMemo(
    () =>
      buildLeagenFlow({
        phoneCalls: Math.round(
          effectivePhoneCalls * (1 - LEAGEN_PHONE_REDUCTION),
        ),
        emailEnquiries: Math.round(
          effectiveEmails * (1 - LEAGEN_EMAIL_REDUCTION),
        ),
        manualPct: Math.round(manualPct * (1 - LEAGEN_MANUAL_REDUCTION)),
        hasOnlineForm,
        takesPhone,
        takesEmail,
        hasReceptionist,
        hasIvr,
        teamMode,
        combinedCount,
        enrolmentOfficers,
        salesOfficers,
        supportOfficers,
      }),
    [
      manualPct,
      effectivePhoneCalls,
      effectiveEmails,
      hasOnlineForm,
      takesPhone,
      takesEmail,
      hasReceptionist,
      hasIvr,
      teamMode,
      combinedCount,
      enrolmentOfficers,
      salesOfficers,
      supportOfficers,
    ],
  );

  // Skeleton while localStorage is being read — avoids the "5-second
  // flash" where the calculator renders with defaults then swaps to
  // the saved values a tick later. Reserves ~800px of vertical space
  // so the page doesn't jump when the real calculator mounts. Matches
  // the real calculator's rounded-3xl border so the transition is a
  // simple fade-in rather than a structural reshuffle.
  if (!hydrated) {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className="border-border bg-background relative flex items-center justify-center overflow-hidden rounded-3xl border"
        style={{ minHeight: 820 }}
      >
        <div className="flex flex-col items-center gap-3">
          <div
            className="h-8 w-8 animate-spin rounded-full border-2 border-transparent"
            style={{
              borderTopColor: '#8570b4',
              borderRightColor: '#8570b4',
            }}
            aria-hidden
          />
          <span className="text-muted-foreground text-xs">
            Loading your saved calculator…
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={calculatorTop}
      className="border-border bg-background scroll-mt-24 overflow-hidden rounded-3xl border"
    >
      <nav
        aria-label="Calculator steps"
        className="flex flex-wrap gap-2 border-b bg-violet-50/50 p-4"
      >
        {steps.map((label, index) => (
          <button
            key={label}
            type="button"
            aria-current={step === index ? 'step' : undefined}
            onClick={() => goToStep(index)}
            className={cn(
              styles.step,
              step === index ? styles.activeStep : styles.inactiveStep,
            )}
          >
            {index + 1}. {label}
          </button>
        ))}
      </nav>
      <div hidden={step !== 0}>
        {/* Intake */}
        <div className="border-border bg-muted/30 border-b px-8 py-8">
          <h3 className="text-muted-foreground mb-6 text-sm font-semibold tracking-wider uppercase">
            Your current student intake
          </h3>

          {/*
          Cascading 2-column layout. Left column gates the right:
            Channels (toggles) — "what intake paths do you accept"
            Volumes (sliders) — "how much comes through each one"
          Turning off a toggle on the left disables / zeros the matching
          slider on the right, so the form reads cause → effect left to
          right instead of flat-mixing toggles and sliders. Every row
          within each column is the same shape (toggle on left, slider
          on right), so the visual rhythm is uniform top-to-bottom.
        */}
          <div className="grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
            {/* LEFT — Channels → Infrastructure → Team stack. Grouping
              reads top-to-bottom as "what enquiry paths, what phone
              plumbing, and what people handle them" — each subsection
              narrows the previous one. Channels at the top because it
              gates the Volumes column across the row; Infrastructure
              in the middle because phone routing only matters when
              phone calls are on; Team at the bottom because roster
              size is the slowest-to-change input and belongs out of
              the way. */}
            <div className="space-y-6">
              <div>
                <h4 className="text-muted-foreground mb-4 text-xs font-semibold tracking-wider uppercase">
                  Channels
                </h4>
                <div className="flex flex-col gap-4">
                  <IntakeToggle
                    label="WordPress website"
                    checked={hasWordpress}
                    onChange={setHasWordpress}
                  />
                  {hasWordpress && (
                    // Indented + compact child toggle — the aXcelerate
                    // Online Enrolment Form is a WordPress-only widget,
                    // so it only makes sense when WordPress is on.
                    // Hiding it entirely (instead of just disabling)
                    // keeps the Channels stack visually shorter when
                    // it's not applicable; indent + smaller switch
                    // communicate the parent → child relationship.
                    <div className="pl-7">
                      <IntakeToggle
                        label="aXcelerate Online Enrolment Form"
                        checked={hasOnlineFormRaw}
                        onChange={setHasOnlineForm}
                        compact
                      />
                    </div>
                  )}
                  <IntakeToggle
                    label="Phone calls"
                    checked={takesPhone}
                    onChange={setTakesPhone}
                  />
                  <IntakeToggle
                    label="Email enquiries"
                    checked={takesEmail}
                    onChange={setTakesEmail}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-muted-foreground mb-4 text-xs font-semibold tracking-wider uppercase">
                  Infrastructure
                </h4>
                <div className="space-y-3">
                  <IntakeToggle
                    label="Receptionist"
                    checked={hasReceptionist}
                    onChange={setHasReceptionist}
                  />
                  <IntakeToggle
                    label="IVR phone system"
                    checked={hasIvr}
                    onChange={setHasIvr}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-muted-foreground mb-4 text-xs font-semibold tracking-wider uppercase">
                  Team
                </h4>
                <div className="border-border inline-flex w-fit rounded-full border p-0.5 text-xs font-medium">
                  <button
                    type="button"
                    onClick={() => setTeamMode('combined')}
                    className={cn(
                      'cursor-pointer rounded-full px-3 py-1 transition-colors',
                      teamMode === 'combined'
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    One person, all roles
                  </button>
                  <button
                    type="button"
                    onClick={() => setTeamMode('separate')}
                    className={cn(
                      'cursor-pointer rounded-full px-3 py-1 transition-colors',
                      teamMode === 'separate'
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    Separate roles
                  </button>
                </div>
                <div className="mt-4 space-y-2">
                  {teamMode === 'combined' ? (
                    <Stepper
                      label="People doing all roles"
                      value={combinedCount}
                      onChange={setCombinedCount}
                      min={1}
                      max={10}
                    />
                  ) : (
                    <>
                      <Stepper
                        label="Enrolment Officers"
                        value={enrolmentOfficers}
                        onChange={setEnrolmentOfficers}
                        min={0}
                        max={20}
                      />
                      <Stepper
                        label="Sales Officers"
                        value={salesOfficers}
                        onChange={setSalesOfficers}
                        min={0}
                        max={20}
                      />
                      <Stepper
                        label="Student Support Officers"
                        value={supportOfficers}
                        onChange={setSupportOfficers}
                        min={0}
                        max={20}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT — Volumes sliders. Laid out as a 2-column grid
              inside the right pane so 5 sliders collapse into 3 rows
              and the overall height of the Volumes column matches the
              compact 4-toggle Channels column on the left, instead of
              stretching to ~5 slider-heights tall. Last row has one
              slider (Manual %) aligned left — leaving the right slot
              empty rather than fabricating filler content. */}
            <div>
              <h4 className="text-muted-foreground mb-4 text-xs font-semibold tracking-wider uppercase">
                Volumes
              </h4>
              {/* Slider order reads as a cause/effect cascade:
                  enrolments → manual %  (how many of those are manual)
                  price       → email    (revenue per, then volume of
                                          lowest-touch enquiry channel)
                  phone       (highest-touch enquiry channel, alone at
                               the bottom so its "heavy" character
                               terminates the list). */}
              <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
                <SliderBlock
                  label="Monthly enrolments"
                  value={monthlyEnrolments}
                  onChange={setMonthlyEnrolments}
                  min={10}
                  max={5_000}
                  step={10}
                  minLabel="10"
                  maxLabel="5,000"
                  format={(v) => v.toLocaleString()}
                />
                <SliderBlock
                  label="Manual enrolment %"
                  value={manualPct}
                  onChange={(v) => setOnlinePct(100 - v)}
                  min={0}
                  max={100}
                  step={5}
                  format={(v) => `${v}%`}
                  disabled={!hasOnlineForm}
                />
                <SliderBlock
                  label="Average course price"
                  value={avgCoursePrice}
                  onChange={setAvgCoursePrice}
                  min={100}
                  max={10_000}
                  step={50}
                  minLabel="A$100"
                  maxLabel="A$10,000"
                  format={(v) => `A$${v.toLocaleString()}`}
                />
                <SliderBlock
                  label="Monthly email enquiries"
                  value={takesEmail ? emailEnquiries : 0}
                  onChange={setEmailEnquiries}
                  min={0}
                  max={3_000}
                  step={10}
                  minLabel="0"
                  maxLabel="3,000"
                  format={(v) => v.toLocaleString()}
                  disabled={!takesEmail}
                />
                <SliderBlock
                  label="Monthly phone calls"
                  value={takesPhone ? phoneCalls : 0}
                  onChange={setPhoneCalls}
                  min={0}
                  max={3_000}
                  step={10}
                  minLabel="0"
                  maxLabel="3,000"
                  format={(v) => v.toLocaleString()}
                  disabled={!takesPhone}
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <span className="text-muted-foreground text-xs">View:</span>
            <div className="border-border flex rounded-full border p-0.5">
              <button
                type="button"
                onClick={() => setMode('normal')}
                className={cn(
                  'cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  mode === 'normal'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Normal
              </button>
              <button
                type="button"
                onClick={() => setMode('detailed')}
                className={cn(
                  'cursor-pointer rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  mode === 'detailed'
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Detailed
              </button>
            </div>
          </div>
        </div>

        {/* Comparison — both columns flex-col so their height stretches
          to match the taller one (via the grid's default align-stretch),
          and so each column's inner content can push the flowchart /
          FlowList down flush to the bottom of the column. That makes
          the two aXcelerate destination cards line up horizontally
          even when the Traditional chart has fewer rows than Leagen. */}
        <div className="grid grid-cols-1 items-stretch divide-y md:grid-cols-2 md:divide-x md:divide-y-0">
          {/* LEFT — Traditional */}
          <div className="flex flex-col px-6 py-8 md:px-8">
            <h3 className="text-muted-foreground text-sm font-semibold tracking-wider uppercase">
              Traditional team
            </h3>
            <TeamComposition
              tone="traditional"
              humans={traditional.staffCount}
            />

            {mode === 'detailed' && (
              <div className="mt-6 hidden flex-1 flex-col md:flex">
                {traditional.staffCount === 0 ? (
                  <EmptyTeamMessage
                    hasEnquiryChannels={takesPhone || takesEmail}
                  />
                ) : (
                  <FlowChart
                    // Remount the chart whenever the set of node+edge IDs
                    // changes — that's the only reliable way to clear
                    // "stuck" edges left over by AnimatePresence when a
                    // node exits between renders. Reflow-only changes (cost
                    // badge, count, label) still update in-place.
                    key={flowStructureKey(traditional)}
                    layout={traditional}
                    tone="traditional"
                    destination={<AxcelerateDestination />}
                  />
                )}
              </div>
            )}
            <FlowList
              layout={traditional}
              tone="traditional"
              className={cn('mt-6', mode === 'detailed' ? 'md:hidden' : '')}
            />
          </div>

          {/* RIGHT — Leagen */}
          <div className="flex flex-col px-6 py-8 md:px-8">
            <h3 className="text-primary text-sm font-semibold tracking-wider uppercase">
              The Leagen way
            </h3>
            <TeamComposition
              tone="leagen"
              humans={leagen.staffCount}
              humansDelta={
                traditional.staffCount > leagen.staffCount
                  ? traditional.staffCount - leagen.staffCount
                  : 0
              }
              hasAi={!leagen.noHumans}
            />

            {mode === 'detailed' && (
              <div className="mt-6 hidden flex-1 flex-col md:flex">
                {leagen.noHumans && <LeagenComplianceNotice />}
                <FlowChart
                  key={flowStructureKey(leagen)}
                  layout={leagen}
                  tone="leagen"
                  destination={<AxcelerateDestination />}
                />
              </div>
            )}
            <FlowList
              layout={leagen}
              tone="leagen"
              className={cn('mt-6', mode === 'detailed' ? 'md:hidden' : '')}
            />
          </div>
        </div>
      </div>
      <div hidden={step === 0}>
        <OutcomeFooter
          view={step}
          traditional={traditional}
          leagen={leagen}
          phoneCalls={phoneCalls}
          emailEnquiries={emailEnquiries}
          manualPct={manualPct}
          takesPhone={takesPhone}
          takesEmail={takesEmail}
          hasOnlineForm={hasOnlineForm}
          monthlyEnrolments={monthlyEnrolments}
          avgCoursePrice={avgCoursePrice}
        />
      </div>
      <div className="flex flex-col items-start justify-between gap-4 border-t px-6 py-5 sm:flex-row sm:items-center">
        <span className="text-muted-foreground text-sm">
          Step {step + 1} of {steps.length}
        </span>
        <div className="flex flex-wrap gap-3">
          {step > 0 && (
            <button
              type="button"
              onClick={() => goToStep(step - 1)}
              className={styles.back}
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={() => goToStep(step < 2 ? step + 1 : 0)}
            className={styles.next}
          >
            {step < 2 ? `Next: ${steps[step + 1]}` : 'Edit calculator'}
          </button>
        </div>
      </div>
      {/* Disclaimer — sits at the very bottom of the calculator card
          so every number above is framed as illustrative, not a
          binding forecast. Covers the four assumption categories the
          calculator rests on (volume reductions, staff throughput,
          hourly rates, and enrolment capacity scaling) so readers
          can map each figure back to the assumption that produced it. */}
      <div className="border-border bg-muted/20 border-t px-6 py-6 md:px-12">
        <details className="text-muted-foreground mx-auto max-w-3xl space-y-3 text-sm leading-relaxed">
          <summary className="cursor-pointer font-medium">
            Estimation disclaimer
          </summary>
          <p>
            Every number on this calculator — the Outcome bars, the Potential
            Growth Capacity cards, the revenue projection — is an illustrative
            estimate based on industry-average assumptions. Your actual results
            will vary depending on your specific course catalogue, enquiry mix,
            staff roles, IVR configuration, and operational choices.
          </p>
          <p>Key assumptions baked in:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              AI absorbs roughly 50% of phone traffic, 75% of email traffic, and
              75% of manual enrolments. Actual reductions vary with the
              complexity and repetitiveness of your enquiries.
            </li>
            <li>
              Average interaction time is 10 minutes per phone call and 5
              minutes per email — industry baselines that don&apos;t capture
              outliers like complex compliance conversations or multi-message
              email threads.
            </li>
            <li>
              Staff time is valued at A$30/hour with a 3× revenue multiplier
              (each employee hour is expected to generate 3× its cost). Both are
              common business benchmarks, not a guarantee of what your team will
              produce.
            </li>
            <li>
              Enrolment growth capacity scales with team size (2× for a solo
              operator up to 6× for a 16-person team) based on compounded
              specialisation effects. Realising this headroom requires you to
              actively route the freed time into revenue-generating work.
            </li>
            <li>
              Annual cost figures are headcount + infrastructure only —
              Leagen&apos;s own pricing is on top. Savings figures assume role
              halving via role sharing; direct headcount reductions are optional
              and separate.
            </li>
          </ul>
          <p>
            Use these numbers as a directional model during your evaluation, not
            as a commitment or a replacement for a tailored quote. Book demo for
            a worked example that reflects your actual setup.
          </p>
        </details>
      </div>
    </div>
  );
}
